<template>
  <b-sidebar
      id="add-new-bid-sidebar"
      :visible="isAddNewBidSidebarActive"
      backdrop
      bg-variant="white"
      no-header
      right
      shadow
      sidebar-class="sidebar-lg"
      @change="(val) => $emit('update:is-add-new-bid-sidebar-active', val)"
      @hidden="resetForm"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Ajouter Offre</h5>

        <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer ref="refFormObserver" #default="{ handleSubmit, invalid }">
        <!-- Form -->
        <b-form
            class="p-2"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
        >
          <!-- Type -->
          <validation-provider
              #default="validationContext"
              name="Type"
              rules="required"
          >
            <b-form-group
                :state="getValidationState(validationContext)"
                label="Type"
                label-for="type"
            >
              <v-select
                  v-model="bidData.type"
                  :clearable="false"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="typeOptions"
                  :reduce="(o) => o.value"
                  input-id="type"
                  placeholder="Choisir le type"
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching">
                    Pas de résultat pour <em>{{ search }}</em
                  >.
                  </template>
                  <em v-else style="opacity: 0.5">Recherche un type...</em>
                </template>
              </v-select>
              <b-form-invalid-feedback
                  :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Status -->
          <validation-provider
              #default="validationContext"
              name="status"
              rules="required"
          >
            <b-form-group
                :state="getValidationState(validationContext)"
                label="Statut"
                label-for="status"
            >
              <v-select
                  v-model="bidData.status"
                  :clearable="false"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="statusOptions"
                  :reduce="(o) => o.key"
                  input-id="status"
                  placeholder="Choisir le statut"
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching">
                    Pas de résultat pour <em>{{ search }}</em
                  >.
                  </template>
                  <em v-else style="opacity: 0.5">Recherche un statut...</em>
                </template>
              </v-select>
              <b-form-invalid-feedback
                  :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Country -->
          <validation-provider
              #default="validationContext"
              name="country"
              rules="required"
          >
            <b-form-group
                :state="getValidationState(validationContext)"
                label="Pays"
                label-for="country"
            >
              <v-select
                  v-model="bidData.country"
                  :clearable="false"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="countries"
                  input-id="country"
                  label="name"
                  placeholder="Choisir le pays"
              >
                <template v-slot:no-options="{ search, searching }">
                  <template v-if="searching">
                    Pas de résultat pour <em>{{ search }}</em
                  >.
                  </template>
                  <em v-else style="opacity: 0.5">Recherche un pays...</em>
                </template>
              </v-select>
              <b-form-invalid-feedback
                  :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Bid Year -->
          <validation-provider #default="validationContext" name="year">
            <b-form-group label="Année" label-for="year">
              <flat-pickr
                  id="year"
                  v-model="bidData.year"
                  :config="flatConfig"
                  class="form-control"
                  placeholder="Année de l'Offre"
              />

              <b-form-invalid-feedback
                  :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
          <!-- Customer -->
          <validation-provider
              #default="validationContext"
              name="customer"
              rules="required"
          >
            <b-form-group
                :state="getValidationState(validationContext)"
                label="Client"
                label-for="customer"
            >
              <v-select
                  v-model="bidData.customer"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="customers"
                  :reduce="(customer) => customer.id"
                  :taggable="false"
                  input-id="customer"
                  label="name"
                  placeholder="Choisir un client"
              >
                <template #no-options>
                  Aucun client trouvé.
                </template>
              </v-select>

              <b-form-invalid-feedback
                  :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Bailleur -->
          <validation-provider
              #default="validationContext"
              name="Bailleur"
              rules="required"
          >
            <b-form-group label="Bailleur" label-for="bailleur">
              <b-form-input
                  id="bailleur"
                  v-model="bidData.details.lessor"
                  :state="getValidationState(validationContext)"
                  trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Concerned departement -->
          <validation-provider
              #default="validationContext"
              name="status"
              rules="required"
          >
            <b-form-group
                :state="getValidationState(validationContext)"
                label="Département Concerné"
                label-for="concerned_department"
            >
              <department-select v-model="bidData.details.concerned_department" :departments="departments" />
              <b-form-invalid-feedback
                  :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Description -->
          <b-form-group label="Description" label-for="bid-description">
            <quill-editor
                id="quil-content"
                v-model="bidData.brief"
                :options="editorOption"
                class="border-bottom-0"
            />
            <div
                id="quill-toolbar"
                class="d-flex justify-content-end border-top-0"
            >
              <!-- Add a bold button -->
              <button class="ql-bold"/>
              <button class="ql-italic"/>
              <button class="ql-underline"/>
              <button class="ql-align"/>
              <button class="ql-link"/>
            </div>
          </b-form-group>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
                :disabled="invalid || isLoading"
                class="mr-2"
                type="submit"
                variant="primary"
            >
              <b-spinner v-if="isLoading" small></b-spinner>
              Ajouter
            </b-button>
            <b-button
                :disabled="isLoading"
                type="button"
                variant="outline-secondary"
                @click="hide"
            >
              Annuler
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import DepartmentSelect from "@/components/departments/DepartmentSelect.vue";
import {useToast} from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
  BCardText,
} from "bootstrap-vue";

import flatPickr from "vue-flatpickr-component";
import {French} from "flatpickr/dist/l10n/fr.js";
import {ValidationProvider, ValidationObserver} from "vee-validate";
import {ref} from "@vue/composition-api";
import {required, alphaNum, email} from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import {quillEditor} from "vue-quill-editor";
import store from "@/store";

export default {
  name: "BidListAddNew",
  components: {
    DepartmentSelect,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BSpinner,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,

    // 3rd party packages
    vSelect,
    flatPickr,
    quillEditor,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isAddNewBidSidebarActive",
    event: "update:is-add-new-bid-sidebar-active",
  },
  props: {
    isAddNewBidSidebarActive: {
      type: Boolean,
      required: true,
    },
    typeOptions: {
      type: Array,
      required: true,
    },
    statusOptions: {
      type: Array,
      required: true,
    },
    customers: {
      type: Array,
      default: () => [],
    },
    countries: {
      type: Array,
      default: () => [],
    },
    departments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      required,
      alphaNum,
    };
  },
  setup(props, {emit}) {
    const toast = useToast();

    const flatConfig = ref({
      wrap: true,
      altFormat: "Y",
      dateFormat: "Y",
      // defaultDate: new Date(),
      locale: French, // locale for this instance only
    });

    const blankBidData = {
      type: "",
      status: "",
      year: null,
      brief: "",
      customer: "",
      details: {
        lessor: "", // bailleur
        concerned_department: "",
        deposit_date: null,
        deposit_place: "",
      },
    };

    const editorOption = {
      modules: {
        toolbar: "#quill-toolbar",
      },
      placeholder: "Écrivez une description brieve de l'Offre",
    };

    const isLoading = ref(false);

    const bidData = ref(JSON.parse(JSON.stringify(blankBidData)));
    const resetBidData = () => {
      bidData.value = JSON.parse(JSON.stringify(blankBidData));
    };

    const onSubmit = () => {
      isLoading.value = true;
      store
          .dispatch("bid/addBid", bidData.value)
          .then((response) => {
            if (response.success) {
              emit("update:isAddNewBidSidebarActive", false);
              emit("refetchData");
              showToast("Offre enregistée avec succès.", "CheckIcon", "success");
              resetBidData()
            } else {
              showToast("Problème d'enregistrement de l'Offre", "AlertTriangleIcon", "danger");
            }
          })
          .catch(({response}) => {
            showToast("Une erreur est survenue lors de l'enregistrement de l'Offre.", "AlertTriangleIcon", "danger");
          })
          .finally(() => {
            isLoading.value = false
          });
    };
    const showToast = (title, icon, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      });
    }

    const {refFormObserver, getValidationState, resetForm} =
        formValidation(resetBidData);

    return {
      flatConfig,
      bidData,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
      isLoading,

      // UI
      editorOption,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>

<style lang="scss" scoped>
@import "~@core/scss/base/bootstrap-extended/include";

.assignee-selector {
  ::v-deep .vs__dropdown-toggle {
    padding-left: 0;
  }
}

#quil-content ::v-deep {
  > .ql-container {
    border-bottom: 0;
  }

  + #quill-toolbar {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
  }
}
</style>
